import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@afosto/app-components-react/Autocomplete';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import sortArrayByProperty from '@afosto/utils/array/sortArrayByProperty';
import { providers as providersApi } from '@afosto/order/api/Providers';

const PaymentProviderFilter = ({ onChange, operator, settings, value, ...props }) => {
  const [availableProviders, setAvailableProviders] = useState([]);
  const [isFetchingProviders, setIsFetchingProviders] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchProviders = async () => {
      setIsFetchingProviders(true);

      try {
        const response = await providersApi.listProviders();
        const { data = [] } = response;
        const formattedProviders = data.reduce(
          (acc, provider) => [
            ...acc,
            {
              ...provider,
              label: provider.name,
              value: provider.code,
            },
          ],
          [],
        );

        if (isMounted) {
          setAvailableProviders(sortArrayByProperty(formattedProviders, 'label'));
        }
      } catch (err) {
        if (isMounted) {
          setAvailableProviders([]);
        }
      } finally {
        if (isMounted) {
          setIsFetchingProviders(false);
        }
      }
    };

    fetchProviders();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleChange = (ev, selectedProviders) => {
    if (onChange && typeof onChange === 'function') {
      onChange(
        ev,
        selectedProviders.map(provider => provider.value),
      );
    }
  };

  const selectedProviders = value.reduce((acc, providerCode) => {
    const provider = findObjectInArray(availableProviders, 'value', `${providerCode}`);

    if (provider) {
      return [...acc, provider];
    }

    return acc;
  }, []);

  return (
    <Autocomplete
      {...props}
      value={selectedProviders}
      onChange={handleChange}
      options={availableProviders}
      loading={isFetchingProviders}
      enableCreate={false}
      autocomplete="off"
      multiple={!operator || ['in', 'not_in'].includes(operator)}
    />
  );
};

PaymentProviderFilter.propTypes = {
  onChange: PropTypes.func,
  operator: PropTypes.string,
  settings: PropTypes.object,
  value: PropTypes.array,
};

PaymentProviderFilter.defaultProps = {
  onChange: undefined,
  operator: undefined,
  settings: {},
  value: [],
};

export default PaymentProviderFilter;
